import React, { useState } from 'react';
import moment from 'moment';
import { useSnackbar } from 'notistack';
import { CircularProgress, Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid, TextField } from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';
import MomentUtils from '@date-io/moment';
import { MuiPickersUtilsProvider, KeyboardDatePicker } from '@material-ui/pickers';
import { fieldDateFormat, backendDateFormat } from '../../../constants';
import { checkApiStatus } from '../../../utils_v2';
import { updateQmeTrackerApi } from '../apiServices';
import ChipList, { formatComponentSerial } from './ChipList';
import { qmeStatusOptions, qmeProgressOptions } from '../';

const QmeTrackerCRU = ({ qmeTracker, mode, users, toggleModalFn, getResponseBack }) => {
  const { enqueueSnackbar } = useSnackbar();
  const [isLoading, setLoading] = useState(false);
  const [error, setError] = useState({});
  const [record, setRecord] = useState({
    ...qmeTracker,
    tracker_status: qmeTracker.tracker_status?.id || '',
    assigned_to: qmeTracker.assigned_to || [],
    component_serial_numbers: qmeTracker.component_serial_numbers || [],
  });
  const onFieldChange = (keyParam, value) => {
    setRecord(prev => ({ ...prev, [keyParam]: value }));
    setError(prev => ({ ...prev, [keyParam]: '' }));
  };
  const updateQmeTrackerFn = () => {
    let validationInputs = {
      workscope: record.workscope?.trim() ? '' : 'Please enter Workscope / Event',
      start_date: record.start_date ? '' : 'Please select Start Date',
      tracker_status: record.tracker_status ? '' : 'Please select Status',
      tracker_progress: qmeProgressOptions.includes(record.tracker_progress) ? '' : 'Please select Progress',
      target_date: record.start_date && record.target_date && moment(record.target_date).isBefore(moment(record.start_date)) ? 'Target date should be greater than start date' : '',
    };
    if (Object.keys(validationInputs).every((k) => validationInputs[k] === '')) {
      setLoading(true);
      updateQmeTrackerApi(record.slug, {
        sysco_claim_number: record.sysco_claim_number,
        qme_type: record.qme_type,
        component_serial_numbers: record.component_serial_numbers,
        mro_facility: record.mro_facility,
        workscope: record.workscope,
        assigned_to: record.assigned_to.map(user => user.id),
        start_date: record.start_date,
        target_date: record.target_date,
        tracker_status: record.tracker_status,
        tracker_progress: record.tracker_progress,
        next_action: record.next_action,
        vendor_remarks: record.vendor_remarks,
        client_remarks: record.client_remarks,
      })
        .then(response => {
          setLoading(false);
          if (checkApiStatus(response)) {
            enqueueSnackbar(response.data.message, { variant: 'success', anchorOrigin: { horizontal: 'right', vertical: 'top' } });
            getResponseBack();
          } else {
            enqueueSnackbar(response?.data?.message || 'Unable to update', { variant: 'error', anchorOrigin: { horizontal: 'right', vertical: 'top' } });
          }
        });
    } else {
      enqueueSnackbar('Please enter the mandatory fields, highlighted in red', { variant: 'error', anchorOrigin: { horizontal: 'right', vertical: 'top' } });
      setError(validationInputs);
    }
  };
  const textField = (keyParam, label, props = {}) => (
    <TextField
      id={keyParam}
      label={label}
      fullWidth
      margin="normal"
      variant="outlined"
      InputLabelProps={{ shrink: true }}
      value={record[keyParam] || ''}
      error={!!error[keyParam]}
      helperText={error[keyParam] || ''}
      onChange={(e) => onFieldChange(keyParam, e.target.value)}
      disabled={mode === 'view'}
      {...props}
    />
  );
  const dateField = (keyParam, label, required) => (
    <MuiPickersUtilsProvider utils={MomentUtils}>
      <KeyboardDatePicker
        margin="normal"
        id={keyParam}
        label={label}
        required={required}
        disabled={mode === 'view'}
        format={fieldDateFormat}
        fullWidth
        clearable={true}
        InputLabelProps={{ shrink: true }}
        inputVariant="outlined"
        value={record[keyParam] || null}
        error={!!error[keyParam]}
        helperText={error[keyParam] || ''}
        onChange={(data) => onFieldChange(keyParam, data ? moment(data).format(backendDateFormat) : null)}
      />
    </MuiPickersUtilsProvider>
  );
  return (
    <Dialog open={true} maxWidth={'md'} className='project-management-modal qme-tracker-modal'>
      <DialogTitle id="scroll-dialog-title">{mode === 'view' ? 'View' : 'Edit'} Tracker Update</DialogTitle>
      <DialogContent dividers={true}>
        <Grid container spacing={1}>
          <Grid item xs={12} md={6}>{textField('project_number', 'Project / Claim Reference', { disabled: true, value: [record.project_number ? `#${record.project_number}` : '', record.name].filter(Boolean).join(' - ') })}</Grid>
          <Grid item xs={12} md={6}>{textField('sysco_claim_number', 'SYSCO Claim Number')}</Grid>
          <Grid item xs={12} md={4}>{textField('lessee', 'Lessee', { disabled: true, value: record.lessee?.name || '' })}</Grid>
          <Grid item xs={12} md={4}>{textField('msn', 'MSN', { disabled: true, value: (record.msn || []).join(', ') })}</Grid>
          <Grid item xs={12} md={4}>{textField('location', 'Location', { disabled: true })}</Grid>
          <Grid item xs={12} md={6}>
            {textField('qme_type', 'QME Type', { disabled: true })}
          </Grid>
          <Grid item xs={12} md={6}>{textField('mro_facility', 'MRO Facility')}</Grid>
          {record.component_serial_numbers.length === 0 ? null :
            <Grid item xs={12}>
              <div className="qme-component-serials">
                <label className="for-label">Component S/N(s)</label>
                <ChipList items={record.component_serial_numbers} getLabel={formatComponentSerial} perRow={8} maxRows={1} moreInline />
              </div>
            </Grid>
          }
          <Grid item xs={12}>{textField('workscope', 'Workscope / Event', { required: true })}</Grid>
          <Grid item xs={12} md={6}>
            <Autocomplete
              multiple
              className="qme-multi-autocomplete"
              disabled
              options={users}
              getOptionLabel={option => option.name}
              getOptionSelected={(option, value) => option.id === value.id}
              id="assigned_to"
              value={record.assigned_to}
              onChange={(e, value) => onFieldChange('assigned_to', value)}
              renderInput={params => <TextField {...params} label="Assigned To" margin="normal" fullWidth InputLabelProps={{ shrink: true }} variant="outlined" />}
            />
          </Grid>
          <Grid item xs={12} md={3}>{dateField('start_date', 'Start Date', true)}</Grid>
          <Grid item xs={12} md={3}>{dateField('target_date', 'Target Date', false)}</Grid>
          <Grid item xs={12} md={4}>
            <Autocomplete
              disabled={mode === 'view'}
              options={qmeStatusOptions}
              getOptionLabel={option => option.label}
              getOptionSelected={(option, value) => option.id === value.id}
              id="tracker_status"
              value={qmeStatusOptions.find(option => option.id === record.tracker_status) || null}
              onChange={(e, value) => onFieldChange('tracker_status', value?.id || '')}
              renderInput={params => <TextField {...params} required label="Status" margin="normal" fullWidth InputLabelProps={{ shrink: true }} variant="outlined" error={!!error.tracker_status} helperText={error.tracker_status || ''} />}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <Autocomplete
              disabled={mode === 'view'}
              options={qmeProgressOptions}
              getOptionLabel={option => `${option}%`}
              id="tracker_progress"
              value={qmeProgressOptions.includes(record.tracker_progress) ? record.tracker_progress : null}
              onChange={(e, value) => onFieldChange('tracker_progress', value)}
              renderInput={params => <TextField {...params} required label="Progress (%)" margin="normal" fullWidth InputLabelProps={{ shrink: true }} variant="outlined" error={!!error.tracker_progress} helperText={error.tracker_progress || ''} />}
            />
          </Grid>
          <Grid item xs={12} md={4}>{textField('next_action', 'Next Action / Due')}</Grid>
          <Grid item xs={12}>{textField('vendor_remarks', 'Vendor Remarks', { multiline: true, rows: 3 })}</Grid>
          <Grid item xs={12}>{textField('client_remarks', 'CSL Remarks', { multiline: true, rows: 3 })}</Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <ul className='list-inline'>
          <li className='list-inline-item'>
            <Button disabled={isLoading} onClick={toggleModalFn} size="small" color="primary" variant="outlined">Cancel</Button>
          </li>
          {mode !== 'view' ?
            <li className='list-inline-item'>
              <Button disabled={isLoading} onClick={updateQmeTrackerFn} size="small" color="primary" variant="contained">
                {isLoading ? <CircularProgress size={20} /> : 'Save'}
              </Button>
            </li> : null
          }
        </ul>
      </DialogActions>
    </Dialog>
  )
}
export default QmeTrackerCRU;
